/**
 * InviteModal Component
 *
 * Modal for inviting new members to a household.
 * Supports two invitation types:
 * - Email invitation: Sends an email with a unique link (7-day expiry)
 * - Code invitation: Generates a shareable 6-char code (24-hour expiry)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Input } from '@/components/ui';
import { cn } from '@/utils/cn';
import { useCreateEmailInvitation, useCreateCodeInvitation } from '@/hooks/useHouseholds';
import { toast } from '@/components/ui/Toaster';

export interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

type InviteTab = 'email' | 'code';

/**
 * Format expiration time
 */
function formatExpiry(expiresAt: string | undefined | null): string {
  if (!expiresAt) {
    return 'unknown';
  }

  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) {
    return 'invalid date';
  }

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  } else {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  }
}

export default function InviteModal({ isOpen, onClose, tenantId }: InviteModalProps) {
  const [activeTab, setActiveTab] = useState<InviteTab>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<{ code: string; expiresAt: string } | null>(
    null
  );
  const [copied, setCopied] = useState(false);

  const emailInputRef = useRef<HTMLInputElement>(null);

  const createEmailInvitation = useCreateEmailInvitation();
  const createCodeInvitation = useCreateCodeInvitation();

  // Focus email input when modal opens with email tab
  useEffect(() => {
    if (isOpen && activeTab === 'email') {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [isOpen, activeTab]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setEmailError(null);
      setGeneratedCode(null);
      setCopied(false);
    }
  }, [isOpen]);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateEmail(email)) {
        return;
      }

      try {
        await createEmailInvitation.mutateAsync({ tenantId, email });
        toast.success('Invitation sent');
        onClose();
      } catch (error) {
        // Log error without PII
        const message =
          error instanceof Error ? error.message : 'Failed to send invitation';
        setEmailError(message);
      }
    },
    [email, tenantId, createEmailInvitation, onClose]
  );

  const handleGenerateCode = useCallback(async () => {
    try {
      const result = await createCodeInvitation.mutateAsync(tenantId);
      setGeneratedCode({ code: result.code, expiresAt: result.expiresAt });
    } catch {
      toast.error('Failed to generate invitation code');
    }
  }, [tenantId, createCodeInvitation]);

  const handleCopyCode = useCallback(async () => {
    if (!generatedCode) return;

    try {
      await navigator.clipboard.writeText(generatedCode.code);
      setCopied(true);
      toast.success('Invitation code copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      toast.error('Failed to copy code');
    }
  }, [generatedCode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Invite Member</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
                aria-label="Close"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-4 flex gap-4 border-b border-gray-200 -mb-px">
              <button
                onClick={() => setActiveTab('email')}
                className={cn(
                  'px-1 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === 'email'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                Email Invitation
              </button>
              <button
                onClick={() => setActiveTab('code')}
                className={cn(
                  'px-1 py-2 text-sm font-medium border-b-2 transition-colors',
                  activeTab === 'code'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                Shareable Code
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {activeTab === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <Input
                    ref={emailInputRef}
                    id="invite-email"
                    type="email"
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) validateEmail(e.target.value);
                    }}
                    className={cn('mt-1', emailError && 'border-red-500')}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'email-error' : undefined}
                  />
                  {emailError && (
                    <p id="email-error" className="mt-1 text-sm text-red-600">
                      {emailError}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    An invitation email will be sent. The link expires in 7 days.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={createEmailInvitation.isPending}
                  >
                    Send Invitation
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                {!generatedCode ? (
                  <>
                    <p className="text-sm text-gray-600">
                      Generate a 6-character code that can be shared with someone to join your
                      household. The code expires in 24 hours.
                    </p>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleGenerateCode}
                        isLoading={createCodeInvitation.isPending}
                      >
                        Generate Code
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 mb-2">Your invitation code:</p>
                      <div className="flex items-center justify-center gap-3">
                        <code className="text-3xl font-mono font-bold tracking-wider text-gray-900 bg-gray-100 px-6 py-3 rounded-lg">
                          {generatedCode.code}
                        </code>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyCode}
                          aria-label={copied ? 'Copied' : 'Copy code'}
                        >
                          {copied ? (
                            <svg
                              className="w-5 h-5 text-green-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                        </Button>
                      </div>
                      <p className="text-sm text-gray-500 mt-4">
                        Expires in {formatExpiry(generatedCode.expiresAt)}
                      </p>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setGeneratedCode(null)}
                      >
                        Generate New Code
                      </Button>
                      <Button type="button" variant="primary" onClick={onClose}>
                        Done
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
